import type { ClientDatabase } from '@app/icare/client/db/schema'
import { CommonDatabaseSchema } from '@app/icare/db/schema'
import type { CareService } from '@app/icare/entities/careService/schema/CareService'
import type { Clinic } from '@app/icare/entities/clinic/schema/Clinic'
import type { CooperationPartner } from '@app/icare/entities/cooperationPartner/schema/CooperationPartner'
import type { CostCarrier } from '@app/icare/entities/costCarrier/schema/CostCarrier'
import type { Doctor } from '@app/icare/entities/doctor/schema/Doctor'
import type { MedicalTechnology } from '@app/icare/entities/medicalTechnology/schema/MedicalTechnology'
import type { PatientIdType } from '@app/icare/entities/patient/schema/Patient'
import type { PatientADTeamRelation } from '@app/icare/entities/patient/schema/PatientADTeam'
import type { PatientAddressRelation } from '@app/icare/entities/patient/schema/PatientAddress'
import type { PatientApplicantRelation } from '@app/icare/entities/patient/schema/PatientApplicant'
import type { PatientCareLevelRelation } from '@app/icare/entities/patient/schema/PatientCareLevel'
import type { PatientCareManagerRelation } from '@app/icare/entities/patient/schema/PatientCareManager'
import type { PatientCareServiceRelation } from '@app/icare/entities/patient/schema/PatientCareService'
import type { PatientClinicRelation } from '@app/icare/entities/patient/schema/PatientClinic'
import type { PatientCooperationPartnerRelation } from '@app/icare/entities/patient/schema/PatientCooperationPartner'
import type { PatientCostCarrierRelation } from '@app/icare/entities/patient/schema/PatientCostCarrier'
import type { PatientDoctorRelation } from '@app/icare/entities/patient/schema/PatientDoctor'
import type { PatientMedicalSupplyAccessRelation } from '@app/icare/entities/patient/schema/PatientMedicalSupplyAccess'
import type { PatientMedicalTechnologyRelation } from '@app/icare/entities/patient/schema/PatientMedicalTechnology'
import type { PatientMobilityRelation } from '@app/icare/entities/patient/schema/PatientMobility'
import type { PatientPharmacyRelation } from '@app/icare/entities/patient/schema/PatientPharmacy'
import type { PatientRelativeRelation } from '@app/icare/entities/patient/schema/PatientRelative'
import type { PatientSeparateResidenceRelation } from '@app/icare/entities/patient/schema/PatientSeparateResidence'
import type { PatientTherapyRelation } from '@app/icare/entities/patient/schema/PatientTherapy'
import type { PatientWeightRelation } from '@app/icare/entities/patient/schema/PatientWeight'
import type { Pharmacy } from '@app/icare/entities/pharmacy/schema/Pharmacy'
import { resolveFields } from '@pkg/db/query'
import { type AliasedRawBuilder, type ExpressionWrapper, expressionBuilder, type SelectExpression, sql } from 'kysely'
import type { ZodDiscriminatedUnion, ZodObject } from 'zod'
import { jsonArrayFrom } from './jsonHelpers'
import { isActive, notDeleted } from './subqueryHelpers'

interface Opts<T extends boolean = boolean> {
  activeOnly?: boolean
  includeTo?: T
}

/** Select all fields for a table from the schema, typed for use with `.select()`. */
export function fields<TB extends keyof ClientDatabase>(table: TB) {
  return resolveFields(
    CommonDatabaseSchema as unknown as Record<string, ZodObject | ZodDiscriminatedUnion>,
    table as string,
  ) as unknown as ReadonlyArray<SelectExpression<ClientDatabase, TB>>
}

function applyFilter<DB, TB extends keyof DB, O>(
  qb: import('kysely').SelectQueryBuilder<DB, TB, O>,
  opts: Opts | undefined,
): import('kysely').SelectQueryBuilder<DB, TB, O> {
  return opts?.activeOnly ? isActive(qb) : notDeleted(qb)
}

type PatientRef = ExpressionWrapper<ClientDatabase, never, PatientIdType>

const defaultPatientIdRef = expressionBuilder<ClientDatabase, 'patient'>().ref('patient.id')

/**
 * Each subquery uses $castTo to short-circuit Kysely's type inference.
 * Without it, the compiler traces through fields() → resolveFields() →
 * SelectExpression<ClientDatabase, TB> for every subquery — 18 times in .all().
 * $castTo tells the compiler the result type directly.
 */

const pharmacies = <T extends boolean>(opts?: Opts<T>, patientRef: PatientRef = defaultPatientIdRef) =>
  jsonArrayFrom(
    applyFilter(
      expressionBuilder<ClientDatabase>()
        .selectFrom('patientPharmacy')
        .select(fields('patientPharmacy'))
        .$if(!!opts?.includeTo, qb => qb.select([toRelation('pharmacy', 'patientPharmacy.toId')]))
        .whereRef('patientPharmacy.patientId', '=', patientRef),
      opts,
    ).$castTo<T extends false ? PatientPharmacyRelation : PatientPharmacyRelation & { to: Pharmacy }>(),
  ).as('pharmacies')

const applicants = (opts?: Opts, patientRef: PatientRef = defaultPatientIdRef) =>
  jsonArrayFrom(
    applyFilter(
      expressionBuilder<ClientDatabase>()
        .selectFrom('patientApplicant')
        .select(fields('patientApplicant'))
        .whereRef('patientApplicant.patientId', '=', patientRef),
      opts,
    ).$castTo<PatientApplicantRelation>(),
  ).as('applicants')

const addresses = (opts?: Opts, patientRef: PatientRef = defaultPatientIdRef) =>
  jsonArrayFrom(
    applyFilter(
      expressionBuilder<ClientDatabase>()
        .selectFrom('patientAddress')
        .select(fields('patientAddress'))
        .whereRef('patientAddress.patientId', '=', patientRef),
      opts,
    ).$castTo<PatientAddressRelation>(),
  ).as('addresses')

const clinics = <T extends boolean>(opts?: Opts<T>, patientRef: PatientRef = defaultPatientIdRef) =>
  jsonArrayFrom(
    applyFilter(
      expressionBuilder<ClientDatabase>()
        .selectFrom('patientClinic')
        .select(fields('patientClinic'))
        .$if(!!opts?.includeTo, qb => qb.select([toRelation('clinic', 'patientClinic.toId')]))
        .whereRef('patientClinic.patientId', '=', patientRef),
      opts,
    ).$castTo<T extends false ? PatientClinicRelation : PatientClinicRelation & { to: Clinic }>(),
  ).as('clinics')

const doctors = <T extends boolean>(opts?: Opts<T>, patientRef: PatientRef = defaultPatientIdRef) =>
  jsonArrayFrom(
    applyFilter(
      expressionBuilder<ClientDatabase>()
        .selectFrom('patientDoctor')
        .select(fields('patientDoctor'))
        .$if(!!opts?.includeTo, qb => qb.select([toRelation('doctor', 'patientDoctor.toId', ['contactPersons'])]))
        .whereRef('patientDoctor.patientId', '=', patientRef),
      opts,
    ).$castTo<T extends false ? PatientDoctorRelation : PatientDoctorRelation & { to: Doctor }>(),
  ).as('doctors')

const careServices = <T extends boolean>(opts?: Opts<T>, patientRef: PatientRef = defaultPatientIdRef) =>
  jsonArrayFrom(
    applyFilter(
      expressionBuilder<ClientDatabase>()
        .selectFrom('patientCareService')
        .select(fields('patientCareService'))
        .$if(!!opts?.includeTo, qb => qb.select([toRelation('careService', 'patientCareService.toId')]))
        .whereRef('patientCareService.patientId', '=', patientRef),
      opts,
    ).$castTo<T extends false ? PatientCareServiceRelation : PatientCareServiceRelation & { to: CareService }>(),
  ).as('careServices')

const medicalTechnologies = <T extends boolean>(opts?: Opts<T>, patientRef: PatientRef = defaultPatientIdRef) =>
  jsonArrayFrom(
    applyFilter(
      expressionBuilder<ClientDatabase>()
        .selectFrom('patientMedicalTechnology')
        .select(fields('patientMedicalTechnology'))
        .$if(!!opts?.includeTo, qb => qb.select([toRelation('medicalTechnology', 'patientMedicalTechnology.toId')]))
        .whereRef('patientMedicalTechnology.patientId', '=', patientRef),
      opts,
    ).$castTo<
      T extends false ? PatientMedicalTechnologyRelation : PatientMedicalTechnologyRelation & { to: MedicalTechnology }
    >(),
  ).as('medicalTechnologies')

const cooperationPartners = <T extends boolean>(opts?: Opts<T>, patientRef: PatientRef = defaultPatientIdRef) =>
  jsonArrayFrom(
    applyFilter(
      expressionBuilder<ClientDatabase>()
        .selectFrom('patientCooperationPartner')
        .select(fields('patientCooperationPartner'))
        .$if(!!opts?.includeTo, qb => qb.select([toRelation('cooperationPartner', 'patientCooperationPartner.toId')]))
        .whereRef('patientCooperationPartner.patientId', '=', patientRef),
      opts,
    ).$castTo<
      T extends false
        ? PatientCooperationPartnerRelation
        : PatientCooperationPartnerRelation & { to: CooperationPartner }
    >(),
  ).as('cooperationPartners')

const costCarriers = <T extends boolean>(opts?: Opts<T>, patientRef: PatientRef = defaultPatientIdRef) =>
  jsonArrayFrom(
    applyFilter(
      expressionBuilder<ClientDatabase>()
        .selectFrom('patientCostCarrier')
        .select(fields('patientCostCarrier'))
        .$if(!!opts?.includeTo, qb => qb.select([toRelation('costCarrier', 'patientCostCarrier.toId')]))
        .whereRef('patientCostCarrier.patientId', '=', patientRef),
      opts,
    ).$castTo<T extends false ? PatientCostCarrierRelation : PatientCostCarrierRelation & { to: CostCarrier }>(),
  ).as('costCarriers')

const separateResidences = (opts?: Opts, patientRef: PatientRef = defaultPatientIdRef) =>
  jsonArrayFrom(
    applyFilter(
      expressionBuilder<ClientDatabase>()
        .selectFrom('patientSeparateResidence')
        .select(fields('patientSeparateResidence'))
        .whereRef('patientSeparateResidence.patientId', '=', patientRef),
      opts,
    ).$castTo<PatientSeparateResidenceRelation>(),
  ).as('separateResidences')

const adTeams = (opts?: Opts, patientRef: PatientRef = defaultPatientIdRef) =>
  jsonArrayFrom(
    applyFilter(
      expressionBuilder<ClientDatabase>()
        .selectFrom('patientAdTeam')
        .select(fields('patientAdTeam'))
        .select([toRelation('adTeam', 'patientAdTeam.toId')])
        .whereRef('patientAdTeam.patientId', '=', patientRef),
      opts,
    ).$castTo<PatientADTeamRelation>(),
  ).as('adTeams')

const careManagers = (opts?: Opts, patientRef: PatientRef = defaultPatientIdRef) =>
  jsonArrayFrom(
    applyFilter(
      expressionBuilder<ClientDatabase>()
        .selectFrom('patientCareManager')
        .select(fields('patientCareManager'))
        .select([toRelation('user', 'patientCareManager.toId')])
        .whereRef('patientCareManager.patientId', '=', patientRef),
      opts,
    ).$castTo<PatientCareManagerRelation>(),
  ).as('careManagers')

const relatives = (opts?: Opts, patientRef: PatientRef = defaultPatientIdRef) =>
  jsonArrayFrom(
    applyFilter(
      expressionBuilder<ClientDatabase>()
        .selectFrom('patientRelative')
        .select(fields('patientRelative'))
        .whereRef('patientRelative.patientId', '=', patientRef),
      opts,
    ).$castTo<PatientRelativeRelation>(),
  ).as('relatives')

const therapies = (opts?: Opts, patientRef: PatientRef = defaultPatientIdRef) =>
  jsonArrayFrom(
    applyFilter(
      expressionBuilder<ClientDatabase>()
        .selectFrom('patientTherapy')
        .select(fields('patientTherapy'))
        .whereRef('patientTherapy.patientId', '=', patientRef),
      opts,
    ).$castTo<PatientTherapyRelation>(),
  ).as('therapies')

const mobilities = (opts?: Opts, patientRef: PatientRef = defaultPatientIdRef) =>
  jsonArrayFrom(
    applyFilter(
      expressionBuilder<ClientDatabase, 'patientMobility'>()
        .selectFrom('patientMobility')
        .select(fields('patientMobility'))
        .whereRef('patientMobility.patientId', '=', patientRef),
      opts,
    ).$castTo<PatientMobilityRelation>(),
  ).as('mobilities')

const weights = (opts?: Opts, patientRef: PatientRef = defaultPatientIdRef) =>
  jsonArrayFrom(
    applyFilter(
      expressionBuilder<ClientDatabase>()
        .selectFrom('patientWeight')
        .select(fields('patientWeight'))
        .whereRef('patientWeight.patientId', '=', patientRef),
      opts,
    ).$castTo<PatientWeightRelation>(),
  ).as('weights')

const careLevels = (opts?: Opts, patientRef: PatientRef = defaultPatientIdRef) =>
  jsonArrayFrom(
    applyFilter(
      expressionBuilder<ClientDatabase>()
        .selectFrom('patientCareLevel')
        .select(fields('patientCareLevel'))
        .whereRef('patientCareLevel.patientId', '=', patientRef),
      opts,
    ).$castTo<PatientCareLevelRelation>(),
  ).as('careLevels')

const medicalSupplyAccess = (opts?: Opts, patientRef: PatientRef = defaultPatientIdRef) =>
  jsonArrayFrom(
    applyFilter(
      expressionBuilder<ClientDatabase>()
        .selectFrom('patientMedicalSupplyAccess')
        .select(fields('patientMedicalSupplyAccess'))
        .whereRef('patientMedicalSupplyAccess.patientId', '=', patientRef),
      opts,
    ).$castTo<PatientMedicalSupplyAccessRelation>(),
  ).as('medicalSupplyAccess')

// ── Owning-relation helpers ────────────────────────────────────────────────

/**
 * Returns a jsonObjectFrom expression that fetches the entity referenced by
 * `toIdRef` as `'to'`. Call directly inside `.select([...])`.
 *
 * @param toTable - The target table (e.g. 'pharmacy')
 * @param toIdRef - SQL reference to the toId column (e.g. 'patientPharmacy.toId')
 */
export function toRelation<ToTable extends keyof ClientDatabase>(
  toTable: ToTable,
  toIdRef: string,
  excludedColumns: string[] = [],
): AliasedRawBuilder<ClientDatabase[ToTable] | null, 'to'> {
  const tableFields = fields(toTable).filter(col => !excludedColumns.includes(String(col).split('.').pop() ?? ''))
  const colPairs = tableFields.flatMap(col => {
    const colName = String(col).split('.').pop()
    if (!colName) return []
    return [sql.lit(colName), sql.ref(`${String(toTable)}.${colName}`)]
  })
  return sql<ClientDatabase[ToTable] | null>`
    (SELECT json_object(${sql.join(colPairs)})
     FROM ${sql.table(String(toTable))}
     WHERE ${sql.ref(`${String(toTable)}.id`)} = ${sql.ref(toIdRef)})
  `.as('to')
}

type PolymorphicTargets = Record<string, keyof ClientDatabase>

/**
 * Returns a sql CASE expression that fetches the correct entity by toId,
 * discriminated by the type column. The result is typed as the union of all
 * possible target entity types.
 *
 * @param typeRef  - SQL reference to the discriminant column (e.g. 'patientApplicant.type')
 * @param toIdRef  - SQL reference to the FK column (e.g. 'patientApplicant.toId')
 * @param targets  - Map of type-value → table name
 */
export function toPolymorphicRelation<Targets extends PolymorphicTargets>(
  typeRef: string,
  toIdRef: string,
  targets: Targets,
): AliasedRawBuilder<ClientDatabase[Targets[keyof Targets]] | null, 'to'> {
  const branches = Object.entries(targets).map(([typeValue, table]) => {
    const tableFields = fields(table)
    const colPairs = tableFields.flatMap(col => {
      const colName = String(col).split('.').pop()
      if (!colName) return []
      return [sql.lit(colName), sql.ref(`${String(table)}.${colName}`)]
    })
    return sql`WHEN ${sql.lit(typeValue)} THEN (
      SELECT json_object(${sql.join(colPairs)})
      FROM ${sql.table(String(table))}
      WHERE ${sql.ref(`${String(table)}.id`)} = ${sql.ref(toIdRef)}
    )`
  })
  return sql<ClientDatabase[Targets[keyof Targets]] | null>`
    CASE ${sql.ref(typeRef)} ${sql.join(branches, sql` `)} END
  `.as('to')
}

// ─────────────────────────────────────────────────────────────────────────────

export const patientRelations = {
  pharmacies,
  applicants,
  addresses,
  clinics,
  doctors,
  careServices,
  medicalTechnologies,
  cooperationPartners,
  costCarriers,
  separateResidences,
  adTeams,
  careManagers,
  relatives,
  therapies,
  mobilities,
  weights,
  careLevels,
  medicalSupplyAccess,
  toRelation,
  toPolymorphicRelation,
  /** Select all patient relations. Use as `.select(patientRelations.all())`. */
  all: (opts?: Opts, patientRef: PatientRef = defaultPatientIdRef) => [
    pharmacies(opts, patientRef),
    applicants(opts, patientRef),
    addresses(opts, patientRef),
    clinics(opts, patientRef),
    doctors(opts, patientRef),
    careServices(opts, patientRef),
    medicalTechnologies(opts, patientRef),
    cooperationPartners(opts, patientRef),
    costCarriers(opts, patientRef),
    separateResidences(opts, patientRef),
    adTeams(opts, patientRef),
    careManagers(opts, patientRef),
    relatives(opts, patientRef),
    therapies(opts, patientRef),
    mobilities(opts, patientRef),
    weights(opts, patientRef),
    careLevels(opts, patientRef),
    medicalSupplyAccess(opts, patientRef),
  ],
}
